import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";

/** How far back the back button remembers (prototype v41). */
export const BACK_DEPTH = 30;

/**
 * The app's back button (prototype v41's `NAV_HIST`): every page the user leaves is remembered, 30
 * deep, and the button takes them to the last one. Home is a destination, not a way back. A
 * browser back to the page on top of the list takes it off, so the two don't disagree.
 */
export function useBackStack(): { canGoBack: boolean; back: () => void } {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const stack = useRef<{ path: string; to: string }[]>([]);
  const prev = useRef<{ path: string; to: string } | null>(null);
  const viaBack = useRef(false);
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const from = prev.current;
    prev.current = { path: location.pathname, to: location.pathname + location.search };
    if (!from || from.path === location.pathname) return;
    const top = stack.current.at(-1);
    if (viaBack.current) viaBack.current = false;
    else if (navType === "POP" && top?.path === location.pathname) stack.current.pop();
    else {
      stack.current.push(from);
      if (stack.current.length > BACK_DEPTH) stack.current.shift();
    }
    setDepth(stack.current.length);
    // a change of query alone (Update Data's ?open=) only updates where "back" returns to
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const back = useCallback(() => {
    const to = stack.current.pop();
    if (!to) return;
    viaBack.current = true;
    setDepth(stack.current.length);
    void navigate(to.to);
  }, [navigate]);

  return { canGoBack: depth > 0, back };
}
